import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import Popover from 'core/components/Popover';
import {
  useFormContext,
} from 'react-hook-form';
import PatientMediaSelect from 'views/EMR/components/PatientMediaSelect';
import {
  Button,
} from '@populateui/ui';
import isNil from 'lodash/isNil';
import {
  ClaimAttachmentDTO,
} from 'dtos';
import compact from 'lodash/compact';
import clsx from 'clsx';
import Tooltip from 'core/components/Tooltip';

const allowedClaimAttachmentMimeTypes = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/tiff',
];

type Props = {
  formKey: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onChange: (mediaId: number) => void;
  mediaId: number | null;
  value: number | null;
};

export default function PatientMediaListPopover({
  formKey,
  anchorEl,
  onClose,
  onChange,
  mediaId,
  value,
}: Props) {
  const { watch } = useFormContext();
  const [patientId, data] = watch(['patientId', formKey]);

  const [
    selectedMultimediaId,
    setSelectedMultimediaId,
  ] = useState<number | null>(value);

  const mediaIds: number[] = useMemo(() => compact(data?.map(
    (item: ClaimAttachmentDTO) => (item?.patientMultiMediaId === mediaId
      ? null
      : item.patientMultiMediaId),
  )), [data, mediaId]);

  useEffect(() => {
    setSelectedMultimediaId(value);
  }, [value]);

  return (
    <Popover
      anchorEl={anchorEl}
      onClose={onClose}
      disableScrollLock
      disableAutoFocus
      disableRestoreFocus
      classes={{
        paper: '!min-w-[800px] !h-[400px]',
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
    >
      <div className="p-4 flex flex-col gap-2 h-full">
        <div className="flex-1 overflow-y-hidden h-full">
          <PatientMediaSelect
            patientId={patientId}
            offsetHeight={0}
            multiple={false}
            disabled={false}
            disabledMediaIds={mediaIds}
            enableTabFilter
            disabledReason="Already attached to this claim"
            allowedMimeTypes={allowedClaimAttachmentMimeTypes}
            allowedMimeTypeReason="Unsupported file type for claim attachment"
            classes={{ table: '!max-h-72' }}
            value={selectedMultimediaId}
            onMediaDoubleClick={(selectedMediaId) => {
              onChange(selectedMediaId);
              onClose();
            }}
            onChange={(value) => {
              if (Array.isArray(value)) {
                setSelectedMultimediaId(value[0] ?? null);
              } else {
                setSelectedMultimediaId(value ?? null);
              }
            }}
          />
        </div>
        <div className="flex gap-2 justify-between items-center">
          <Button
            onClick={onClose}
            variant="outlined"
            color="secondary"
          >
            Close
          </Button>
          <Tooltip title="Select a media to attach" hide={!isNil(selectedMultimediaId)}>
            <span>
              <Button
                onClick={() => {
                  if (!isNil(selectedMultimediaId)) {
                    onChange(selectedMultimediaId);
                    onClose();
                  }
                }}
                variant="contained"
                color="primary"
                disabled={isNil(selectedMultimediaId)}
                className={clsx({
                  '!bg-gray-300 !text-gray-700 !opacity-50 pointer-events-auto !cursor-not-allowed': isNil(selectedMultimediaId),
                })}
              >
                Attach
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
    </Popover>
  );
}
